import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileUp, Download, Loader2 } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * Bulk import CSV button used per tab in the Show Library editor.
 * Props:
 *   templateId: string
 *   kind: "characters" | "scenes" | "songs"
 *   onImported: () => void
 */
export default function CsvImportButton({ templateId, kind, onImported }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const label = kind[0].toUpperCase() + kind.slice(1);
  const cols = kind === "characters"
    ? ["name (required)", "voice_type", "description"]
    : ["name (required)", "act", "pages", "characters", "notes"];

  const sampleRows = kind === "characters"
    ? [
        "name,voice_type,description",
        "The Baker,Baritone,A humble baker longing for a child",
        "The Baker's Wife,Mezzo-Soprano,Adventurous and pragmatic",
      ]
    : [
        "name,act,pages,characters,notes",
        kind === "songs"
          ? "Prologue: Into the Woods,1,1-24,Baker|Baker's Wife|Cinderella,Opening number"
          : "Act 1 Scene 1: The Cottage,1,1-8,Baker|Baker's Wife,Establishes the story",
      ];

  const download = () => {
    const csv = sampleRows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stagecall-${kind}-sample.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // axios inherits the auth interceptor from `api`
      const { data } = await api.post(
        `/platform/show-library/${templateId}/import-csv?kind=${kind}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      toast.success(
        data.inserted
          ? `Imported ${data.inserted} ${kind}${data.skipped ? ` (skipped ${data.skipped})` : ""}`
          : `No new ${kind} imported${data.skipped ? ` — ${data.skipped} skipped` : ""}`,
      );
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => { setOpen(false); reset(); };

  return (
    <>
      <button
        data-testid={`csv-import-${kind}-btn`}
        onClick={() => setOpen(true)}
        className="h-9 px-3 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-1.5 text-sm font-medium"
      >
        <FileUp className="w-4 h-4" /> Import CSV
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Import {label} from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-white/10 bg-[#121212] p-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                Expected columns
              </div>
              <ul className="mt-1.5 text-xs text-zinc-300 space-y-1">
                {cols.map((c) => (
                  <li key={c} className="font-mono text-[11px]">· {c}</li>
                ))}
              </ul>
              {kind !== "characters" && (
                <div className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                  <code className="text-zinc-300">characters</code> accepts values separated by{" "}
                  <code className="text-zinc-300">|</code> or{" "}
                  <code className="text-zinc-300">,</code>.
                </div>
              )}
              <button
                data-testid={`csv-sample-${kind}-btn`}
                onClick={download}
                className="mt-3 text-xs text-gold inline-flex items-center gap-1 hover:underline"
              >
                <Download className="w-3.5 h-3.5" /> Download sample CSV
              </button>
            </div>

            <div className="rounded-lg border border-dashed border-white/15 bg-[#0F0F0F] p-4 text-center">
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                data-testid={`csv-file-${kind}-input`}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              <button
                onClick={() => inputRef.current?.click()}
                className="h-9 px-3 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1.5"
              >
                <FileUp className="w-4 h-4" /> {file ? "Change file" : "Choose file"}
              </button>
              <div className="mt-2 text-[11px] text-zinc-500">
                {file ? file.name : "No file selected"}
              </div>
            </div>

            {result && (
              <div
                data-testid={`csv-result-${kind}`}
                className="rounded-lg border border-gold/30 bg-gold/5 p-3 text-xs text-zinc-200"
              >
                Imported <span className="text-gold font-semibold">{result.inserted}</span>{" "}
                {kind}. Skipped <span className="text-zinc-400">{result.skipped}</span>{" "}
                {result.skipped === 1 ? "row" : "rows"} (duplicates or empty names).
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={close}
              className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5"
            >
              Close
            </button>
            <button
              data-testid={`csv-run-${kind}-btn`}
              onClick={run}
              disabled={!file || busy}
              style={goldBtn}
              className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              {busy ? "Importing…" : "Import"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
